/**
 * CR-7320: import ERP fault-code mapping rows from CSV — the layout of the
 * proposed mapping file (erp_code, erp_standard_text, proposed_fault_group,
 * proposed_fault_category). Upload runs a dry run first; nothing is saved
 * until the preview has been reviewed and "Save N changes" is pressed.
 */
import { useState } from 'react';
import { qualitySettingsAPI } from '../../../lib/api';
import type { FaultCodeImportResult } from '../../../lib/api';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Input } from '../../ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

interface FaultCodeMappingImportDialogProps {
  open: boolean;
  organisation?: string;
  onClose: () => void;
  onSaved: () => void;
}

const ACTION_STYLE: Record<string, string> = {
  create: 'text-emerald-700',
  update: 'text-amber-700',
  unchanged: 'text-muted-foreground',
  error: 'text-destructive',
};

export default function FaultCodeMappingImportDialog({
  open, organisation, onClose, onSaved,
}: FaultCodeMappingImportDialogProps) {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<FaultCodeImportResult | null>(null);
  const [busy, setBusy] = useState(false);

  const reset = () => { setFile(null); setPreview(null); };

  const run = async (dryRun: boolean) => {
    if (!file) return;
    try {
      setBusy(true);
      const resp = await qualitySettingsAPI.importFaultCodeMappings(file, dryRun, organisation);
      if (dryRun) {
        setPreview(resp.data);
      } else {
        const saved = resp.data.saved;
        toast.success(`Mapping imported: ${saved?.created ?? 0} added, ${saved?.updated ?? 0} changed (provisional)`);
        reset();
        onSaved();
      }
    } catch (err: unknown) {
      const data = (err as { response?: { data?: { file?: string | string[] } } })?.response?.data;
      const msg = Array.isArray(data?.file) ? data?.file[0] : data?.file;
      toast.error(msg || 'Could not read the CSV');
    } finally {
      setBusy(false);
    }
  };

  const changes = preview ? preview.summary.create + preview.summary.update : 0;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); onClose(); } }}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Import ERP fault-code mapping (CSV)</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Columns: <code>erp_code</code>, <code>erp_standard_text</code>, <code>proposed_fault_group</code>,{' '}
            <code>proposed_fault_category</code>. Group and category must already exist in your fault taxonomy.
            Added or changed rows are saved as provisional.
          </p>
          <Input
            type="file"
            accept=".csv,text/csv"
            data-testid="fault-mapping-csv"
            onChange={(e) => { setFile(e.target.files?.[0] ?? null); setPreview(null); }}
          />
          {preview && (
            <>
              <p className="text-sm" data-testid="fault-mapping-preview-summary">
                Preview: {preview.summary.create} to add, {preview.summary.update} to change,{' '}
                {preview.summary.unchanged} unchanged, {preview.summary.error} with errors.
              </p>
              <div className="max-h-[360px] overflow-auto border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Line</TableHead>
                      <TableHead>ERP code</TableHead>
                      <TableHead>Now</TableHead>
                      <TableHead>After import</TableHead>
                      <TableHead>Result</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.rows.map((row) => (
                      <TableRow key={`${row.line}-${row.erp_code}`}>
                        <TableCell className="text-xs">{row.line}</TableCell>
                        <TableCell className="font-mono text-xs">{row.erp_code || '—'}</TableCell>
                        <TableCell className="text-xs">{row.current ?? '—'}</TableCell>
                        <TableCell className="text-xs">{row.fault_group} / {row.fault_category}</TableCell>
                        <TableCell className={`text-xs ${ACTION_STYLE[row.action]}`}>
                          {row.action}{row.message ? `: ${row.message}` : ''}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { reset(); onClose(); }}>Cancel</Button>
          {!preview ? (
            <Button onClick={() => run(true)} disabled={!file || busy}>
              {busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Preview changes
            </Button>
          ) : (
            <Button onClick={() => run(false)} disabled={busy || changes === 0}>
              {busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Save {changes} change{changes === 1 ? '' : 's'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
